import React, { useRef, useState } from 'react';
import { Button, Popconfirm, Space } from 'antd';
import { Link } from 'react-router-dom';
import { cuerpoError, estadoError, leerAccionesCaso, mensajeError, nuevoIdPeticion, registrarOportunidad } from '../../api/feriasApi';
import type { AccionesCaso, ActividadReciente, TipoOportunidad, ValorCampo } from '../../api/feriasApi';
import { leerCatalogos, leerYo, Pestania } from '../../api/oportunidadesApi';
import type { Catalogos, EmpleadoUsuario } from '../../api/oportunidadesApi';
import { FctAviso } from '../../fct/react';
import { NuevaOportunidadModal } from '../Oportunidades/Modales';
import { motivoEnPantalla } from './motivos';
import { inicialesDe } from './oportunidadIniciales';

/** Cómo se nombra y se identifica cada tipo: su pestaña de Oportunidades y su clase en la actividad del cliente. */
const TIPOS: Record<TipoOportunidad, { nombre: string; plural: string; pestania: Pestania; clase: 'VENTA' | 'COMPRA' }> = {
  venta: { nombre: 'oportunidad de venta', plural: 'oportunidades de venta', pestania: Pestania.Venta, clase: 'VENTA' },
  compra: { nombre: 'oportunidad de compra', plural: 'oportunidades de compra', pestania: Pestania.Compra, clase: 'COMPRA' },
};

interface Props {
  tipo: TipoOportunidad;
  caso: AccionesCaso;
  /** Lo verificado de la ficha: sus casillas y sus observaciones (null si no está verificada). */
  valores: Record<string, ValorCampo> | null;
  /** La actividad reciente del cliente, para avisar de las oportunidades que ya tiene. */
  actividad: ActividadReciente | null;
  /** La cabecera del caso tras registrar el número. */
  onCaso: (caso: AccionesCaso) => void;
}

/**
 * Una sección de oportunidad de «Acciones» (M6 §3.2 y §3.3): si ya se generó, su número y un enlace a su
 * detalle en Oportunidades, y el botón desactivado (P4); si no, un aviso con las oportunidades de ese tipo
 * que el cliente ya tiene, y «Generar», que, tras confirmarlo, abre «Nueva oportunidad» de Oportunidades con
 * los valores de §3.2 (D-FE-41). Al guardarse allí, su número se registra en el paso del caso con un id de
 * petición (D39); si el registro falla, se puede reintentar con el mismo id, sin crear otra oportunidad.
 * Si otro supervisor registró antes otra del mismo tipo (409), la sección muestra la registrada, no deja
 * generar más, y pide descartar en Oportunidades la que se acaba de crear.
 */
export const OportunidadCaso: React.FC<Props> = ({ tipo, caso, valores, actividad, onCaso }) => {
  const { nombre, plural, pestania, clase } = TIPOS[tipo];
  const [oportunidades, setOportunidades] = useState<{ catalogos: Catalogos; yo: EmpleadoUsuario | null } | null>(null);
  const [abierta, setAbierta] = useState(false);
  const [preparando, setPreparando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Una oportunidad ya creada cuyo número no se pudo registrar: se reintenta con la misma petición.
  const [pendiente, setPendiente] = useState<{ numero: number; idPeticion: string } | null>(null);
  // Una oportunidad creada que sobra, porque el caso ya tenía otra del mismo tipo: hay que descartarla.
  const [sobrante, setSobrante] = useState<number | null>(null);
  const registrando = useRef(false);

  const numero = tipo === 'venta' ? caso.numeroVenta : caso.numeroCompra;
  const existentes = (actividad?.elementos ?? []).filter((e) => e.clase === clase);

  // Antes de abrir la ventana, la cabecera otra vez: si otro supervisor ya generó una de este tipo, se
  // muestra esa y no se abre nada. La primera vez, también los catálogos y la ficha de empleado.
  const abrir = async () => {
    setError(null);
    setPreparando(true);
    try {
      const actual = await leerAccionesCaso(caso.idCaso);
      if ((tipo === 'venta' ? actual.numeroVenta : actual.numeroCompra) !== null) {
        onCaso(actual);
        return;
      }
      if (!oportunidades) {
        const [catalogos, yo] = await Promise.all([leerCatalogos(), leerYo().catch(() => null)]);
        setOportunidades({ catalogos, yo });
      }
      setAbierta(true);
    } catch (e) {
      setError(motivoEnPantalla(mensajeError(e)));
    } finally {
      setPreparando(false);
    }
  };

  // Registra el número en el paso del caso; un fallo deja la petición pendiente para reintentarla.
  const registrar = async (registro: { numero: number; idPeticion: string }) => {
    if (registrando.current) {
      return;
    }
    registrando.current = true;
    setError(null);
    try {
      onCaso(await registrarOportunidad(caso.idCaso, tipo, registro.numero, registro.idPeticion));
      setPendiente(null);
    } catch (e) {
      // 409 con la cabecera: el caso ya tiene otra de este tipo (P4). Se muestra esa, y la recién creada sobra.
      const yaRegistrada = estadoError(e) === 409 ? (cuerpoError(e).caso as AccionesCaso | undefined) : undefined;
      if (yaRegistrada) {
        setPendiente(null);
        setSobrante(registro.numero);
        onCaso(yaRegistrada);
      } else {
        setPendiente(estadoError(e) === 409 ? null : registro);
        setError(motivoEnPantalla(mensajeError(e)));
      }
    } finally {
      registrando.current = false;
    }
  };

  // «Nueva oportunidad» sigue montada también cuando el caso ya tiene su número: tras el alta, el email de bienvenida
  // (#255) vive dentro de ella y se cerraría con ella.
  const ventanaAlta = oportunidades && (
    <NuevaOportunidadModal
      abierto={abierta}
      pestaniaInicial={pestania}
      pestaniaFija
      catalogos={oportunidades.catalogos}
      yo={oportunidades.yo}
      iniciales={inicialesDe(tipo, caso, valores, oportunidades.catalogos)}
      onCerrar={() => setAbierta(false)}
      onCreada={(_, id) => {
        setAbierta(false);
        void registrar({ numero: id, idPeticion: nuevoIdPeticion() });
      }}
    />
  );

  if (numero !== null) {
    return (
      <Space direction="vertical" style={{ width: '100%' }}>
        {sobrante !== null && sobrante !== numero && (
          <FctAviso tipo="error" titulo={`La ${nombre} nº ${sobrante} que acaba de crear sobra`}>
            Otro usuario registró antes la nº {numero} para este caso, y solo se genera una por tipo. Descarte la nº {sobrante} en{' '}
            <Link to={`/oportunidades?${tipo}=${sobrante}`}>Oportunidades</Link>.
          </FctAviso>
        )}
        <FctAviso tipo="exito" titulo={`Generada la ${nombre} nº ${numero}`}>
          <Link to={`/oportunidades?${tipo}=${numero}`}>Ver su detalle en Oportunidades</Link>
        </FctAviso>
        <Button disabled>Generar {nombre}</Button>
        {ventanaAlta}
      </Space>
    );
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {existentes.length > 0 && (
        <FctAviso tipo="aviso" titulo={`El cliente ya tiene ${existentes.length === 1 ? `una ${nombre}` : `${existentes.length} ${plural}`} en su actividad reciente`}>
          {existentes.map((e) => `nº ${e.id}`).join(', ')}.
        </FctAviso>
      )}
      {error && (
        <FctAviso tipo="error" titulo={pendiente ? `La ${nombre} nº ${pendiente.numero} se creó, pero no se ha registrado en el caso` : 'No se ha podido completar la operación'}>
          {error}
        </FctAviso>
      )}
      {pendiente ? (
        <Button type="primary" onClick={() => void registrar(pendiente)}>
          Volver a registrar la {nombre} nº {pendiente.numero}
        </Button>
      ) : (
        <Popconfirm
          title={`¿Generar una ${nombre} para este cliente?`}
          description="Se abre la ventana de Oportunidades con los datos de la ficha; se guarda allí."
          okText="Sí, generar"
          cancelText="No"
          onConfirm={() => void abrir()}
        >
          <Button loading={preparando}>Generar {nombre}</Button>
        </Popconfirm>
      )}
      {ventanaAlta}
    </Space>
  );
};
